// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner on Quest, 2026-11-04: "The ayni chatbot window is drawn at the
 * same place but behind the playback track, draw it vertically next to the
 * sphere or to the side of the user if inside the sphere"): the AYNI
 * chatbot's window in the headset. A tall panel of its own, beside the globe
 * on the viewer's left past the legend (`vrSession` stands it; inside a globe
 * past 2 m, at the viewer's side, `vrInsideView`), instead of the one-line
 * strip that hung under the dock, where the date track covered it and long
 * replies were cut to a line.
 *
 * It shows what the dock's mic is doing (`voiceCaption`), the question the
 * reply answers, the reply wrapped onto as many lines as fit, and a line
 * when no voice could read the reply out. Nothing on it is pressed; the
 * dock's mic drives it.
 */

import type * as THREE from 'three'
import { t } from '../i18n'
import { PANEL_COLORS, createCanvasPanel, type PanelPainter } from './vrPanelKit'
import { voiceCaption, type VrUnreadReason, type VrVoiceState } from './vrHud'

/** What the window says when a reply was not read aloud, by reason (owner on Quest, 2026-11-05). */
const UNREAD_TEXT = {
  blocked: 'vr.voice.unread.blocked',
  quota: 'vr.voice.unread.quota',
  server: 'vr.voice.unread.server',
  'no-voice': 'vr.voice.unread.noVoice',
} as const satisfies Record<VrUnreadReason, string>

/** World size: 1 canvas pixel = 0.5 mm. */
export const CHAT_PANEL_WIDTH = 0.36
export const CHAT_PANEL_HEIGHT = 0.56
const CANVAS_W = 720
const CANVAS_H = 1120
const PAD = 36

/** What the window says, or null when it hides. */
export interface ChatPanelContent {
  label: string
  /** The question the reply answers, when there is a reply. */
  question: string | null
  text: string
  /** Why the reply was not read aloud, or null when it was (or is being). */
  unread: VrUnreadReason | null
}

export function chatPanelContent(voice: VrVoiceState | null | undefined): ChatPanelContent | null {
  const caption = voiceCaption(voice)
  if (!caption || !voice) return null
  // Listening again after a reply, the reply stays (with its question) under the label.
  const replying = voice.phase === 'speaking' || voice.phase === 'idle' || (voice.phase === 'listening' && !!voice.question)
  const question = replying && voice.question ? voice.question : null
  return { label: caption.label, question, text: caption.text, unread: replying ? voice.unread ?? null : null }
}

/** Draw the window: the phase, the question, the reply wrapped, and the note. */
export function drawChatPanel(p: PanelPainter<never>, content: ChatPanelContent): void {
  p.frame(28)
  const width = CANVAS_W - PAD * 2
  p.icon('mic', PAD + 18, 54, 34, PANEL_COLORS.accent)
  p.text(content.label, PAD + 52, 54, { size: 30, weight: 700, color: PANEL_COLORS.accentText, maxWidth: width - 52 })
  let y = 112
  if (content.question) {
    const lines = p.wrap(content.question, width - 28, 3, 26, 500)
    const boxH = lines.length * 36 + 28
    p.box(PAD, y, width, boxH, 16, PANEL_COLORS.surface)
    lines.forEach((line, i) => p.text(line, PAD + 14, y + 32 + i * 36, { size: 26, color: PANEL_COLORS.textSecondary }))
    y += boxH + 26
  }
  const noteH = content.unread ? 96 : 0
  const room = Math.max(1, Math.floor((CANVAS_H - PAD - noteH - y) / 42))
  const lines = content.text.split('\n').flatMap((l) => (l.trim() ? p.wrap(l, width, room, 30, 500) : []))
  const shown = lines.slice(0, room)
  // Cut short: the last line shown ends in an ellipsis.
  if (lines.length > room && shown.length) shown[shown.length - 1] = `${shown[shown.length - 1]!.replace(/…$/, '')}…`
  shown.forEach((line, i) => p.text(line, PAD, y + 20 + i * 42, { size: 30, color: PANEL_COLORS.text, maxWidth: width }))
  if (content.unread) {
    const ny = CANVAS_H - PAD - noteH + 12
    const note = p.wrap(t(UNREAD_TEXT[content.unread]), width - 28, 2, 24, 600)
    p.box(PAD, ny, width, noteH - 12, 16, 'rgba(255, 204, 102, 0.12)', PANEL_COLORS.warning, 2)
    note.forEach((line, i) => p.text(line, PAD + 14, ny + 28 + i * 32, { size: 24, weight: 600, color: PANEL_COLORS.warning }))
  }
}

export interface VrChatPanelHandle {
  readonly mesh: THREE.Mesh
  /** Show what a voice turn says, or hide with null; redrawn only on a change. */
  set(voice: VrVoiceState | null): void
  isVisible(): boolean
  dispose(): void
}

export function createVrChatPanel(THREE_: typeof THREE): VrChatPanelHandle {
  const panel = createCanvasPanel<never>(THREE_, {
    widthM: CHAT_PANEL_WIDTH,
    heightM: CHAT_PANEL_HEIGHT,
    canvasW: CANVAS_W,
    canvasH: CANVAS_H,
  })
  panel.mesh.name = 'vr-chat'
  panel.mesh.visible = false
  let drawn = ''
  return {
    mesh: panel.mesh,
    set(voice) {
      const content = chatPanelContent(voice)
      panel.mesh.visible = content !== null
      const sig = content ? JSON.stringify(content) : ''
      if (!content || sig === drawn) {
        if (!content) drawn = ''
        return
      }
      drawn = sig
      panel.draw((p) => drawChatPanel(p, content))
    },
    isVisible: () => panel.mesh.visible,
    dispose() {
      panel.dispose()
    },
  }
}
